import { type ReactNode, Suspense } from "react";
import { ErrorBoundary } from "react-error-boundary";
import { useTranslation } from "react-i18next";
import {
  Navigate,
  Outlet,
  type RouteObject,
  useMatches,
  useNavigate,
} from "react-router";

import type { RouteHandle } from "shell/platform/types/types";

import { Loading } from "design-system/components/Loading";
import { NotFoundPage } from "design-system/components/NotFoundPage";
import { RouteErrorBoundary } from "design-system/components/RouteErrorBoundary";
import { AuthorizationGuard } from "shell/console/components/AuthorizationGuard";
import { AuthShell } from "shell/console/components/AuthShell";
import { BaseLayout } from "shell/console/components/BaseLayout";
import { DefaultLandingPage } from "shell/console/components/DefaultLandingPage";
import { FailedToLoad } from "shell/console/components/FailedToLoad";
import { LoginRedirect } from "shell/console/components/LoginRedirect";
import { Shell } from "shell/console/components/Shell";
import { AuditLog } from "shell/console/features/AuditLog";
import { FeatureFlags } from "shell/console/features/FeatureFlags";
import { FeatureRequest } from "shell/console/features/FeatureRequest";
import { AuditLog as SandboxAdminAuditLog } from "shell/console/features/sandboxAdmin/AuditLog";
import { DomainLists as SandboxAdminDomainLists } from "shell/console/features/sandboxAdmin/DomainLists";
import { UserManagement as SandboxAdminUserManagement } from "shell/console/features/sandboxAdmin/UserManagement";
import { useHideChrome } from "shell/console/hooks/useHideChrome";
import { featureFlags } from "shell/platform/config/featureFlags";
import { pathsSchema } from "shell/platform/config/paths";
import {
  INTERNAL_ROLES,
  PORTAL_ROLES,
} from "shell/platform/constants/constants";
import { logError } from "shell/platform/lib/analytics/rum";

import { withProductArea } from "./utils/productArea";

const NotFoundRoute = () => {
  const { t } = useTranslation(["common"]);
  const navigate = useNavigate();

  return (
    <NotFoundPage
      title={t("common:components.notFound.title")}
      description={t("common:components.notFound.description")}
      homeButtonLabel={t("common:components.notFound.backToHome")}
      onNavigateHome={() => navigate(pathsSchema.index)}
    />
  );
};

export const LayoutResolver = ({ children }: { children: ReactNode }) => {
  const matches = useMatches();

  const hideChrome = useHideChrome();

  const hideSidebar = matches.some(
    match => (match.handle as RouteHandle)?.hideSidebar,
  );

  if (hideChrome) return <>{children}</>;

  return <BaseLayout isSidebarVisible={!hideSidebar}>{children}</BaseLayout>;
};

type RouteSlot = "standalone" | "app" | "portal" | "console";

interface ModuleRoutes {
  enabled?: boolean;
  id: string;
  load: () => Promise<RouteObject[]>;
  slot: RouteSlot;
  // Set to false to leave the module's routes out of amplitude.
  tracked?: boolean;
}

const getModuleRoutes = (isDemoRoutesEnabled: boolean): ModuleRoutes[] => [
  {
    id: "login",
    slot: "standalone",
    load: async () => (await import("common-services/routes")).loginRoutes,
  },
  {
    id: "sandbox",
    slot: "app",
    enabled: featureFlags.enableSandbox,
    tracked: false,
    load: async () => (await import("sandbox/routes")).sandboxRoutes,
  },
  {
    id: "thredd-portal",
    slot: "portal",
    load: async () => (await import("thredd-portal/routes")).portalRoutes,
  },
  {
    id: "organization",
    slot: "portal",
    load: async () =>
      (await import("common-services/routes")).organizationRoutes,
  },
  {
    id: "client-lifecycle",
    slot: "console",
    load: async () => (await import("client-lifecycle/routes")).routes,
  },
  {
    id: "common-services",
    slot: "console",
    load: async () => (await import("common-services/routes")).routes,
  },
  {
    id: "instrument-issuance",
    slot: "console",
    load: async () => (await import("instrument-issuance/routes")).routes,
  },
  {
    id: "customer-account",
    slot: "console",
    load: async () => (await import("customer-account/routes")).routes,
  },
  {
    id: "composed",
    slot: "console",
    load: async () =>
      (await import("shell/routes/composedRoutes")).getComposedRoutes(),
  },
  {
    id: "customer-simulation",
    slot: "console",
    enabled: isDemoRoutesEnabled,
    load: async () =>
      (await import("demo-hub/customer-simulation/routes")).routes,
  },
  {
    id: "storefront-simulation",
    slot: "console",
    enabled: isDemoRoutesEnabled,
    load: async () =>
      (await import("demo-hub/storefront-simulation/routes")).routes,
  },
  {
    id: "ledger-engine-simulation",
    slot: "console",
    enabled: isDemoRoutesEnabled,
    load: async () =>
      (await import("demo-hub/ledger-engine-simulation/routes")).routes,
  },
  {
    id: "badger",
    slot: "console",
    enabled: isDemoRoutesEnabled,
    load: async () => (await import("demo-hub/badger/routes")).routes,
  },
];

const loadModuleRoutes = async (
  modules: ModuleRoutes[],
): Promise<Record<RouteSlot, RouteObject[]>> => {
  const loaded = await Promise.all(
    modules
      .filter(({ enabled = true }) => enabled)
      .map(async ({ id, slot, load, tracked = true }) => ({
        slot,
        routes: tracked ? withProductArea(await load(), id) : await load(),
      })),
  );

  const bySlot: Record<RouteSlot, RouteObject[]> = {
    standalone: [],
    app: [],
    portal: [],
    console: [],
  };
  for (const { slot, routes } of loaded) bySlot[slot].push(...routes);

  return bySlot;
};

export const getRouterConfig = async ({
  isDemoRoutesEnabled = featureFlags.enableDemoRoutes,
}: {
  isDemoRoutesEnabled?: boolean;
} = {}): Promise<RouteObject[]> => {
  const moduleRoutes = await loadModuleRoutes(
    getModuleRoutes(isDemoRoutesEnabled),
  );

  return [
    // standalone routes - no providers needed
    ...moduleRoutes.standalone,
    {
      path: pathsSchema.signOutRedirect,
      element: <Navigate to={pathsSchema.index} />,
      errorElement: <RouteErrorBoundary />,
    },
    // auth callback routes - AuthGate and ThemeProvider needed
    {
      element: (
        <AuthShell>
          <Outlet />
        </AuthShell>
      ),
      errorElement: <RouteErrorBoundary />,
      children: [
        {
          path: pathsSchema.signInRedirect,
          element: <LoginRedirect />,
          errorElement: <RouteErrorBoundary />,
        },
        {
          path: pathsSchema.cognitoCallback,
          element: <LoginRedirect />,
          errorElement: <RouteErrorBoundary />,
        },
      ],
    },
    // full app routes - uses Shell and all providers
    {
      element: (
        <Shell>
          <Outlet />
        </Shell>
      ),
      errorElement: <RouteErrorBoundary />,
      children: [
        ...moduleRoutes.app,
        {
          path: pathsSchema.index,
          element: (
            <LayoutResolver>
              <AuthorizationGuard>
                <Suspense fallback={<Loading isFullScreen size="large" />}>
                  <ErrorBoundary fallback={<FailedToLoad />} onError={logError}>
                    <Outlet />
                  </ErrorBoundary>
                </Suspense>
              </AuthorizationGuard>
            </LayoutResolver>
          ),
          errorElement: <RouteErrorBoundary />,
          children: [
            {
              index: true,
              element: <DefaultLandingPage />,
            },
            {
              element: <Outlet />,
              handle: { roles: PORTAL_ROLES },
              children: [
                {
                  path: pathsSchema.featureRequest.index,
                  element: <FeatureRequest />,
                },
                ...moduleRoutes.portal,
              ],
            },
            // console routes
            {
              handle: { roles: INTERNAL_ROLES },
              element: <Outlet />,
              children: [
                {
                  path: pathsSchema.auditLog.index,
                  element: <AuditLog />,
                },
                {
                  path: pathsSchema.featureFlags.index,
                  element: <FeatureFlags />,
                },
                ...(featureFlags.enableSandbox
                  ? [
                      {
                        path: pathsSchema.sandboxAdmin.audit,
                        element: <SandboxAdminAuditLog />,
                      },
                      {
                        path: pathsSchema.sandboxAdmin.userManagement,
                        element: <SandboxAdminUserManagement />,
                      },
                      {
                        path: pathsSchema.sandboxAdmin.domainLists,
                        element: <SandboxAdminDomainLists />,
                      },
                    ]
                  : []),
                ...moduleRoutes.console,
              ],
            },
            {
              path: "*",
              element: <NotFoundRoute />,
              errorElement: <RouteErrorBoundary />,
            },
          ],
        },
      ],
    },
  ];
};
